import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useT } from '../i18n';
import { searchPlaces, shortLabel, type GeocodeResult } from '../lib/nominatim';
import './AddressSearch.css';

interface AddressSearchProps {
  /** The stored address string, controlled by the parent. */
  value: string;
  error?: string | undefined;
  /** Free-text edits (the manual fallback). Clears any picked coordinates. */
  onChange: (address: string) => void;
  /** A suggestion was chosen — carries the formatted label and coordinates. */
  onSelect: (result: GeocodeResult) => void;
}

type Status = 'idle' | 'loading' | 'error';

const DEBOUNCE_MS = 450;
const MIN_QUERY = 3;

/**
 * The address field: one text input the owner can type freely into, with a
 * Bucharest-restricted suggestion list under it. Picking a suggestion stores its
 * coordinates; typing by hand is the manual fallback and stores just the string.
 *
 * The input value is owned by the parent (it IS the stored address), so this is
 * a controlled combobox rather than one holding its own query state.
 */
export function AddressSearch({ value, error, onChange, onSelect }: AddressSearchProps) {
  const t = useT();
  const listboxId = useId();
  const inputId = useId();
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const abortRef = useRef<AbortController | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Set when a suggestion is chosen, so the resulting value change does not
  // immediately fire a fresh search for the label we just filled in.
  const justPickedRef = useRef(false);

  useEffect(() => {
    if (justPickedRef.current) {
      justPickedRef.current = false;
      return;
    }

    const trimmed = value.trim();
    if (trimmed.length < MIN_QUERY) {
      abortRef.current?.abort();
      setResults([]);
      setStatus('idle');
      setOpen(false);
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('loading');
      searchPlaces(trimmed, controller.signal)
        .then((found) => {
          if (controller.signal.aborted) return;
          setResults(found);
          setStatus('idle');
          setOpen(true);
          setActiveIndex(found.length > 0 ? 0 : -1);
        })
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setStatus('error');
          setResults([]);
          setOpen(true);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Dismiss the listbox on an outside click.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const choose = useCallback(
    (result: GeocodeResult) => {
      justPickedRef.current = true;
      setOpen(false);
      setActiveIndex(-1);
      onSelect(result);
    },
    [onSelect],
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      const picked = results[activeIndex];
      if (picked) {
        event.preventDefault();
        choose(picked);
      }
    }
  };

  const clear = () => {
    justPickedRef.current = false;
    onChange('');
    setResults([]);
    setOpen(false);
    setStatus('idle');
  };

  const showEmpty = open && status === 'idle' && results.length === 0 && value.trim().length >= MIN_QUERY;

  return (
    <div className="field address-search" ref={rootRef}>
      <label className="field__label" htmlFor={inputId}>
        {t.step1.addressLabel}
      </label>

      <div className="address-search__control">
        <input
          id={inputId}
          className="input address-search__input"
          type="text"
          role="combobox"
          data-field="address"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-invalid={Boolean(error)}
          aria-describedby={[error ? errorId : null, helpId].filter(Boolean).join(' ') || undefined}
          aria-activedescendant={
            activeIndex >= 0 && results[activeIndex] ? `${listboxId}-${activeIndex}` : undefined
          }
          placeholder={t.step1.addressPlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
        />
        {value.length > 0 && (
          <button
            type="button"
            className="address-search__clear"
            onClick={clear}
            aria-label={t.step1.addressClear}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>

      <p className="field__help" id={helpId}>
        {t.step1.addressHint}
      </p>

      <span className="visually-hidden" role="status" aria-live="polite">
        {status === 'loading' ? t.step1.addressSearching : ''}
      </span>

      {open && (
        <ul className="address-search__results" id={listboxId} role="listbox">
          {status === 'error' && <li className="address-search__message">{t.step1.addressError}</li>}
          {showEmpty && <li className="address-search__message">{t.step1.addressNoResults}</li>}
          {results.map((result, index) => (
            <li key={result.id} role="presentation">
              <button
                type="button"
                id={`${listboxId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={
                  index === activeIndex
                    ? 'address-search__option address-search__option--active'
                    : 'address-search__option'
                }
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(result)}
              >
                {shortLabel(result.label)}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="field__error" id={errorId}>
          <span className="visually-hidden">{t.validation.fieldError}</span>
          <span aria-hidden="true">↳</span>
          {error}
        </p>
      )}
    </div>
  );
}
